// Land Intelligence Map View — CONUS-bounded SVG with parcel pins.
// Lightweight: no leaflet/mapbox dependency. Pins are positioned by an
// equirectangular projection scaled to the CONUS bounding box. Good
// enough for a portfolio overview; full PostGIS + tile basemap lands
// when the platform needs interactive zoom/pan.
//
// Brain: [skills/land-intelligence].
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || '';

// CONUS bounding box (decimal degrees). Excludes AK/HI.
const CONUS = {
  lat_min: 24.5,   // tip of Florida Keys
  lat_max: 49.5,   // northern Maine / WA border
  lon_min: -125.0, // Pacific NW coast
  lon_max: -66.5,  // Maine/Atlantic coast
};

const SVG_WIDTH = 1100;
const SVG_HEIGHT = 600;

// Equirectangular projection (lat/lon → SVG x/y) clamped to CONUS.
function project(latitude, longitude) {
  const x = ((longitude - CONUS.lon_min) / (CONUS.lon_max - CONUS.lon_min)) * SVG_WIDTH;
  const y = ((CONUS.lat_max - latitude) / (CONUS.lat_max - CONUS.lat_min)) * SVG_HEIGHT;
  return { x, y };
}

// Pipeline-stage → pin color
const STAGE_COLOR = {
  discovered: '#71717a',  // zinc-500
  screening: '#0ea5e9',   // sky-500
  qualified: '#6366f1',   // indigo-500
  diligence: '#8b5cf6',   // violet-500
  loi: '#f59e0b',         // amber-500
  contract: '#10b981',    // emerald-500
  closed: '#059669',      // emerald-600
  passed: '#a1a1aa',      // zinc-400
};

const REFERENCE_MARKETS = [
  { name: 'NoVA Loudoun', lat: 39.05, lon: -77.50 },
  { name: 'Phoenix metro', lat: 33.45, lon: -112.07 },
  { name: 'Dallas/FW', lat: 32.78, lon: -96.80 },
  { name: 'Quincy/PNW', lat: 47.23, lon: -119.85 },
];

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function LandMap() {
  const [parcels, setParcels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoverId, setHoverId] = useState(null);
  const [showRefMarkets, setShowRefMarkets] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(`${API_BASE}/land/parcels?limit=500`, { headers: authHeaders() });
      if (!resp.ok) throw new Error(`parcels: ${resp.status}`);
      const body = await resp.json();
      setParcels(body.parcels || []);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Filter to parcels actually inside CONUS — others are off-map.
  const visible = useMemo(() => {
    return parcels.filter((p) =>
      p.latitude >= CONUS.lat_min && p.latitude <= CONUS.lat_max &&
      p.longitude >= CONUS.lon_min && p.longitude <= CONUS.lon_max
    );
  }, [parcels]);

  // Pin radius scales with target_mw (sqrt for area-proportional).
  // Default to 100 MW if matrix not populated.
  const radiusFor = (parcel) => {
    const mw = parcel.power_solution_matrix?.target_mw ?? 100;
    return Math.max(4, Math.min(20, Math.sqrt(mw) * 0.9));
  };

  const hovered = visible.find((p) => p.parcel_id === hoverId);

  return (
    <div className="min-h-screen bg-zinc-50 p-6 lg:p-10">
      <div className="mx-auto max-w-[1300px]">
        <header className="mb-6 flex items-end justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
              Land Intelligence — Map View
            </p>
            <h1 className="mt-1 text-3xl font-bold tracking-tight text-zinc-900">
              CONUS Parcel Map
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-zinc-600">
              All active parcels colored by pipeline stage, sized by target
              MW. Click a pin to open Parcel Detail. Reference markets shown
              as squares for context. Full interactive map with PostGIS and
              tile basemap lands later — this is a lightweight portfolio
              overview without a 200KB+ dep.
            </p>
          </div>
          <Link
            to="/land-intelligence/pipeline"
            className="rounded-xl border border-zinc-300 bg-white px-4 py-2 text-sm font-semibold text-zinc-700 shadow-sm transition-colors hover:bg-zinc-100"
          >
            ← Pipeline
          </Link>
        </header>

        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-zinc-200 bg-white p-3 shadow-sm">
          <label className="flex items-center gap-2 text-sm text-zinc-700">
            <input
              type="checkbox"
              checked={showRefMarkets}
              onChange={(e) => setShowRefMarkets(e.target.checked)}
            />
            Show reference markets
          </label>
          <span className="ml-auto text-sm text-zinc-600">
            {visible.length} of {parcels.length} parcels in CONUS
          </span>
        </div>

        {error && (
          <div className="mb-4 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}

        <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
          <div className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm">
            {loading && parcels.length === 0 ? (
              <div className="flex h-[600px] items-center justify-center text-sm text-zinc-500">
                Loading map…
              </div>
            ) : (
              <svg
                viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
                className="h-full w-full"
                style={{ aspectRatio: `${SVG_WIDTH} / ${SVG_HEIGHT}` }}
              >
                {/* Subtle CONUS background */}
                <rect x={0} y={0} width={SVG_WIDTH} height={SVG_HEIGHT} fill="#f4f4f5" />
                <rect
                  x={0} y={0} width={SVG_WIDTH} height={SVG_HEIGHT}
                  fill="none" stroke="#a1a1aa" strokeWidth={1}
                />
                {/* Major-state graticule (rough) */}
                <Graticule />

                {/* Reference market squares */}
                {showRefMarkets && REFERENCE_MARKETS.map((m) => {
                  const { x, y } = project(m.lat, m.lon);
                  return (
                    <g key={m.name}>
                      <rect x={x - 6} y={y - 6} width={12} height={12} fill="none" stroke="#71717a" strokeWidth={1.5} />
                      <text x={x + 10} y={y + 4} fontSize={11} fill="#52525b">
                        {m.name}
                      </text>
                    </g>
                  );
                })}

                {/* Parcel pins */}
                {visible.map((p) => {
                  const { x, y } = project(p.latitude, p.longitude);
                  const r = radiusFor(p);
                  const color = STAGE_COLOR[p.pipeline_stage] || STAGE_COLOR.discovered;
                  const isHover = p.parcel_id === hoverId;
                  return (
                    <Link
                      key={p.parcel_id}
                      to={`/land-intelligence/parcel/${encodeURIComponent(p.parcel_id)}`}
                    >
                      <circle
                        cx={x} cy={y} r={r}
                        fill={color}
                        opacity={isHover ? 1 : 0.7}
                        stroke="#27272a"
                        strokeWidth={isHover ? 2 : 1}
                        onMouseEnter={() => setHoverId(p.parcel_id)}
                        onMouseLeave={() => setHoverId(null)}
                        style={{ cursor: 'pointer' }}
                      />
                    </Link>
                  );
                })}
              </svg>
            )}
          </div>

          <aside className="space-y-4">
            <Legend />
            {hovered ? (
              <HoverCard parcel={hovered} />
            ) : (
              <div className="rounded-2xl border border-zinc-200 bg-white p-4 text-sm text-zinc-600 shadow-sm">
                Hover over a pin to see parcel details, click to open the
                full record.
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}

function Graticule() {
  // Lightweight reference lines for orientation. 5-degree intervals.
  const lines = [];
  for (let lat = 25; lat <= 50; lat += 5) {
    const { y } = project(lat, CONUS.lon_min);
    lines.push(
      <line key={`p-${lat}`}
            x1={0} y1={y} x2={SVG_WIDTH} y2={y}
            stroke="#e4e4e7" strokeWidth={0.5} strokeDasharray="2 4" />
    );
  }
  for (let lon = -120; lon <= -70; lon += 10) {
    const { x } = project(CONUS.lat_min, lon);
    lines.push(
      <line key={`m-${lon}`}
            x1={x} y1={0} x2={x} y2={SVG_HEIGHT}
            stroke="#e4e4e7" strokeWidth={0.5} strokeDasharray="2 4" />
    );
  }
  return <g>{lines}</g>;
}

function Legend() {
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">
        Pipeline stage
      </h3>
      <ul className="space-y-1 text-sm text-zinc-700">
        {Object.entries(STAGE_COLOR).map(([stage, color]) => (
          <li key={stage} className="flex items-center gap-2">
            <span
              className="inline-block h-3 w-3 rounded-full"
              style={{ backgroundColor: color }}
            />
            <span className="capitalize">{stage}</span>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs italic text-zinc-500">
        Pin size scales with target MW (√MW).
      </p>
    </div>
  );
}

function HoverCard({ parcel }) {
  const score = parcel.composite_score ?? 0;
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
        Hovered parcel
      </p>
      <p className="mt-1 font-mono text-sm font-bold text-zinc-900">{parcel.parcel_id}</p>
      <p className="text-xs text-zinc-600">
        {parcel.county}, {parcel.state} · {parcel.acreage?.toFixed(0) ?? '—'} ac
      </p>
      <dl className="mt-3 space-y-1 text-sm">
        <div className="flex justify-between">
          <dt className="text-zinc-600">Stage</dt>
          <dd className="font-semibold capitalize text-zinc-900">{parcel.pipeline_stage}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-zinc-600">Score</dt>
          <dd className="font-mono text-zinc-900">{score.toFixed(0)}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-zinc-600">Speed</dt>
          <dd className="font-mono text-zinc-900">
            {parcel.speed_to_mw_months != null ? `${parcel.speed_to_mw_months.toFixed(0)} mo` : '—'}
          </dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-zinc-600">Rev accel</dt>
          <dd className="font-mono text-zinc-900">
            {parcel.revenue_acceleration_value_usd
              ? `$${formatNumber(parcel.revenue_acceleration_value_usd)}`
              : '—'}
          </dd>
        </div>
      </dl>
      <Link
        to={`/land-intelligence/parcel/${encodeURIComponent(parcel.parcel_id)}`}
        className="mt-3 inline-block rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-xs font-semibold text-zinc-700 hover:bg-zinc-100"
      >
        Open detail →
      </Link>
    </div>
  );
}

function formatNumber(n) {
  if (n == null) return '—';
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n.toFixed(0);
}
